import { inject, injectable, injectFromBase, unmanaged } from 'inversify';
import type { AxiosError, AxiosResponse } from 'axios';
import { BaseApiService } from '@kiplin/web-common/architecture-core';
import type { JwtService } from '@shared/common/network/domain/service/jwt.service';
import { NetworkDependencyTypes } from '@shared/common/network/network.dependency-types';
import { axiosResponseErrorInterceptor } from '@shared/common/network/data/service/axios-response-error-interceptor.service';
import type { InternalAxiosRequestConfigWithRetry } from '@shared/common/network/data/model/internal-axios-request-config-with-retry.model';
import { TranslationDependencyTypes } from '@kiplin/web-common/translation';
import type { Composer } from 'vue-i18n';

@injectable()
@injectFromBase()
export abstract class AbstractCockpitApiService extends BaseApiService {
  @inject(NetworkDependencyTypes.JwtService)
  private readonly jwtService!: JwtService;
  @inject(TranslationDependencyTypes.i18n)
  private readonly i18n!: Composer;

  protected constructor(@unmanaged() baseUrl: string) {
    super(baseUrl);

    this.axios.interceptors.request.use(async (config) => {
      const jwt = await this.jwtService.getActiveEncodedJwt();
      config.headers.set('Authorization', `Bearer ${jwt}`);
      config.headers.set('Accept-Language', this.i18n.locale.value);

      return config;
    });
    // @see support uri parameter https://github.com/axios/axios/issues/706
    this.axios.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error: AxiosError) => {
        axiosResponseErrorInterceptor(error);

        // cast du error.config comme défini car non défini checké dans axiosResponseErrorInterceptor
        const config = error.config as InternalAxiosRequestConfigWithRetry;
        const status = error.response?.status ?? null;
        if (status !== 403 || (config.attemptNumber ?? 0) >= 1) {
          return Promise.reject(error);
        }
        const jwt = await this.jwtService.getActiveEncodedJwt({ forceRefresh: true });
        config.headers.set('Authorization', `Bearer ${jwt}`);
        config.headers.set('Accept-Language', this.i18n.locale.value);
        return this.axios.request(config);
      },
    );
  }
}
